import React, { FunctionComponent, useEffect, useState } from "react";
import {
    ButtonProps,
    CommandBarButton,
    createErrorFailure,
    DefaultButton,
    FormValidationSummary,
    LoadingButton,
    Modal,
    Stack,
    Text,
    validationFailed,
    ValidationResult
} from "sprint-common-web";

import { MergeTvSeriesRequest } from "../../api/MergeTvSeriesRequest";
import { useAssetsManageTvSeriesApi } from "../../api/useAssetsManageTvSeriesApi";
import { ValidateMergeTvSeriesRequest } from "../../api/ValidateMergeTvSeriesRequest";
import { mapToTvSeriesLisingItem, TvSeriesListingItem } from "../TvSeriesListingItem";

export interface MergeTvSeriesCommandBarButtonProps extends ButtonProps {
    tvSeriesToMerge: TvSeriesListingItem[];
    updateListAfterMerge(sourceTvSeries: TvSeriesListingItem[], newTvSeries: TvSeriesListingItem): void;
    clearTvSeriesToMerge(): void;
}

export const MergeTvSeriesCommandBarButton: FunctionComponent<MergeTvSeriesCommandBarButtonProps> = (
    props: MergeTvSeriesCommandBarButtonProps
) => {
    const { mergeTvSeries, validateMergeTvSeries } = useAssetsManageTvSeriesApi();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!isModalOpen) return;

        setIsValidationLoading(true);

        validateMergeTvSeries(createValidateMergeRequest(props.tvSeriesToMerge))
            .then((res) => setValidationResult(res))
            .catch((err) => setValidationResult(validationFailed([createErrorFailure(JSON.stringify(err))])))
            .finally(() => setIsValidationLoading(false));
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const isMergeButtonDisabled = () => {
        return props.tvSeriesToMerge.length < 2;
    };

    const handleMergeButtonClick = async (): Promise<void> => {
        setIsLoading(true);
        const response = await mergeTvSeries(createValidateMergeRequest(props.tvSeriesToMerge));

        props.updateListAfterMerge(props.tvSeriesToMerge, mapToTvSeriesLisingItem(response.item, true));
        props.clearTvSeriesToMerge();
        closeModal();
        setIsLoading(false);
    };

    const buttonDisabled = props.disabled || isMergeButtonDisabled();
    const isMergeConfirmDisabled = isLoading || isValidationLoading || !validationResult?.isValid;

    return (
        <>
            <CommandBarButton onClick={openModal} text="Merge" icon="Merge" disabled={buttonDisabled} />
            <Modal isBlocking={true} isOpen={isModalOpen} headerText="Merge TvSeries" onDismiss={closeModal}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text>You are about to Merge:</Text>
                    {props.tvSeriesToMerge.map((tvSeries: TvSeriesListingItem) => {
                        return <Text key={tvSeries.id}>{tvSeries.title}</Text>;
                    })}
                </Stack>
                <Stack tokens={{ padding: "10px 0" }}>
                    <FormValidationSummary validationResult={validationResult} isValidating={isValidationLoading} />
                </Stack>
                <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="end" horizontalAlign="end">
                    <DefaultButton text="Cancel" onClick={closeModal} disabled={isLoading} />
                    <LoadingButton
                        text="Merge"
                        isLoading={isLoading}
                        onClick={handleMergeButtonClick}
                        disabled={isMergeConfirmDisabled}
                    />
                </Stack>
            </Modal>
        </>
    );
};

const createValidateMergeRequest = (
    tvSeriesListingItems: TvSeriesListingItem[]
): ValidateMergeTvSeriesRequest | MergeTvSeriesRequest => {
    return {
        firstTvSeriesId: tvSeriesListingItems[0].id,
        secondTvSeriesId: tvSeriesListingItems[1].id
    };
};
